import { LineageNode } from "../fileManagement.types";
import { LineageTree } from "@/entities/FileService/File.types";
import { FileSource } from "@/entities/FileService/File.model";

function getFileType(name: string): "raw" | "cleaned" | "labeled" {
    if (name.startsWith("raw-")) return "raw";
    if (name.startsWith("cl-")) return "cleaned";
    if (name.startsWith("labeled-")) return "labeled";
    return "raw";
}

function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
}

function extractDateFromFilename(filename: string): string | null {
    const match = filename.match(/^(?:cl|labeled)-(\d{8})/);
    if (!match) return null;

    const dateStr = match[1];
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);

    return `${month}/${day}/${year}`;
}

function convertFileToLineageNode(
    file: LineageTree,
    companyId: string,
    meta?: {
        source: FileSource;
        createdAt: string;
        createdBy?: string;
    },
): LineageNode {
    const fileType = getFileType(file.name);

    let emphasizedText = "";
    let text = "";

    switch (fileType) {
        case "raw":
            if (
                meta?.source === FileSource.USER_UPLOADED ||
                meta?.source === FileSource.SYSTEM_UPLOADED
            ) {
                emphasizedText = "File uploaded";
                text = "";
            } else {
                emphasizedText = "File created";
                text = "from Codat pull";
            }
            break;
        case "cleaned":
            emphasizedText = "File cleaned";
            text = "";
            break;
        case "labeled":
            emphasizedText = "File labeled";
            text = "";
            break;
    }

    const lineageNode: LineageNode = {
        id: file.id,
        emphasizedText,
        text,
        status: "completed",
    };

    if (fileType === "raw" && meta?.createdAt) {
        lineageNode.date = formatDate(meta.createdAt);
    }

    if (fileType === "cleaned" || fileType === "labeled") {
        const extractedDate = extractDateFromFilename(file.name);
        if (extractedDate) {
            lineageNode.date = extractedDate;
        }

        lineageNode.link = {
            label: "View file in labeling",
            href: `/beta/companies/${companyId}/labeling/files/${file.id}`,
        };
    }

    if (file.descendants && file.descendants.length > 0) {
        lineageNode.nodes = file.descendants.map((descendant) =>
            convertFileToLineageNode(descendant, companyId),
        );
    }

    if (fileType === "labeled") {
        const dataIngestionNode: LineageNode = {
            id: `${file.id}-ingestion`,
            text: "Data ingestion status coming soon",
            status: "in-progress",
        };
        lineageNode.nodes = lineageNode.nodes
            ? [...lineageNode.nodes, dataIngestionNode]
            : [dataIngestionNode];
    }

    return lineageNode;
}

export function parseFileTree(
    fileTree: LineageTree,
    meta: {
        source: FileSource;
        createdAt: string;
        createdBy?: string;
        companyId: string;
    },
): LineageNode[] {
    const rootNode = convertFileToLineageNode(fileTree, meta.companyId, {
        source: meta.source,
        createdAt: meta.createdAt,
        createdBy: meta.createdBy,
    });
    const cleanedNodes = rootNode.nodes || [];
    delete rootNode.nodes;

    if (cleanedNodes.length === 1) {
        const singleCleanedNode = cleanedNodes[0];
        const labeledNodes = singleCleanedNode.nodes || [];
        delete singleCleanedNode.nodes;

        const flattenedNodes: LineageNode[] = [rootNode, singleCleanedNode];

        labeledNodes.forEach((labeledNode) => {
            const ingestionNodes = labeledNode.nodes || [];
            delete labeledNode.nodes;
            flattenedNodes.push(labeledNode);
            flattenedNodes.push(...ingestionNodes);
        });

        return flattenedNodes;
    }

    return [rootNode, ...cleanedNodes];
}
